import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import { useCases } from '../di/useCases';
import { updateLanguageFromPreferences } from '../../locales';
import { GEO } from '../constants/geo';

// Définition des préférences par défaut
const DEFAULT_PREFERENCES = {
  language: 'fr',
  theme: 'system',
  contentLanguages: ['fr'],
  maxDistance: GEO.DEFAULT_RADIUS_KM,
  profileVisibility: 'followers',
  activityVisibility: 'everyone',
  shareLocation: false,
  shareActivity: true,
  // Position GPS mise à jour manuellement par l'utilisateur (source de vérité pour le feed et le chat)
  currentPosition: null,
  notifications: {
    email: true,
    push: true,
    sms: false,
    whatsapp: false
  },
  notificationTypes: {
    newMessage: true,
    newFollower: true,
    activityReminder: true,
    nearbyActivity: true
  }
};

const PreferencesContext = createContext(null);
const STORAGE_KEY = 'user_preferences';

export const PreferencesProvider = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const loadFirestorePreferences = useCallback(async (uid) => {
    try {
      const firestorePrefs = await useCases.getUserPreferences.execute(uid);
      return firestorePrefs;
    } catch (error) {
      console.error('Erreur lors du chargement des préférences Firestore:', error);
      setError(error);
      return null;
    }
  }, []);

  const initializePreferences = useCallback(async (uid, initialData = {}) => {
    try {
      setIsLoading(true);
      const defaultPrefs = {
        ...DEFAULT_PREFERENCES,
        language: initialData.language || DEFAULT_PREFERENCES.language
      };
      const savedPrefs = await useCases.initializeUserPreferences.execute(uid, defaultPrefs);
      setPreferences(savedPrefs);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedPrefs));
      setIsInitialized(true);
      setError(null);
      updateLanguageFromPreferences({ language: savedPrefs.language });
    } catch (error) {
      console.error("Erreur lors de l'initialisation des préférences:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Chargement des préférences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setIsLoading(true);
        
        // 1. Essayer de charger depuis localStorage d'abord
        const localPrefs = localStorage.getItem(STORAGE_KEY);
        const parsedLocalPrefs = localPrefs ? JSON.parse(localPrefs) : null;
        
        let finalPreferences;
        
        if (parsedLocalPrefs) {
          // Si des préférences locales existent, les utiliser
          finalPreferences = parsedLocalPrefs;
          setPreferences(parsedLocalPrefs);
        } else if (user?.uid) {
          // Sinon, essayer de charger depuis Firestore
          const firestorePrefs = await loadFirestorePreferences(user.uid);
          if (firestorePrefs) {
            finalPreferences = firestorePrefs;
            setPreferences(firestorePrefs);
            // Sauvegarder dans localStorage pour accès futur
            localStorage.setItem(STORAGE_KEY, JSON.stringify(firestorePrefs));
          } else {
            // Si pas de préférences Firestore, utiliser les défauts
            finalPreferences = DEFAULT_PREFERENCES;
            setPreferences(DEFAULT_PREFERENCES);
          }
        } else {
          // Si non authentifié, utiliser les défauts
          finalPreferences = DEFAULT_PREFERENCES;
          setPreferences(DEFAULT_PREFERENCES);
        }
        
        setIsInitialized(true);
        setError(null);
        
        // Appliquer la langue dès que les préférences sont chargées
        if (finalPreferences?.language) {
          // Utiliser directement les préférences chargées pour garantir la cohérence
          updateLanguageFromPreferences({ language: finalPreferences.language });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des préférences:', error);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [user, loadFirestorePreferences]);

  // 2. Mettre à jour les préférences
  const updatePreference = useCallback(async (key, value) => {
    // Si l'utilisateur n'est pas connecté, ne pas sauvegarder dans Firestore
    if (!user?.uid) {
      setError(new Error("Utilisateur non connecté"));
      return false;
    }

    try {
      // Préparer les nouvelles préférences
      const newPreferences = key.includes('.')
        ? (() => {
            const keyParts = key.split('.');
            return {
              ...preferences,
              [keyParts[0]]: {
                ...preferences[keyParts[0]],
                [keyParts[1]]: value
              }
            };
          })()
        : {
            ...preferences,
            [key]: value
          };

      // 2a. Sauvegarder dans Firestore d'abord
      const success = await useCases.updateUserPreferences.execute(user.uid, newPreferences);
      
      // 2b. Si la sauvegarde Firestore réussit, mettre à jour l'état local et localStorage
      if (success) {
        setPreferences(newPreferences);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newPreferences));
        setError(null);
        return true;
      } else {
        setError(new Error("Échec de la sauvegarde des préférences"));
        return false;
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour des préférences:', err);
      setError(err);
      return false;
    }
  }, [user, preferences]);

  // 2. Mettre à jour plusieurs préférences à la fois
  const updatePreferences = useCallback(async (updates) => {
    // Si l'utilisateur n'est pas connecté, ne pas sauvegarder dans Firestore
    if (!user?.uid) {
      setError(new Error("Utilisateur non connecté"));
      return false;
    }

    try {
      const newPreferences = {
        ...preferences,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // 2a. Sauvegarder dans Firestore d'abord
      const success = await useCases.updateUserPreferences.execute(user.uid, newPreferences);
      
      // 2b. Si la sauvegarde Firestore réussit, mettre à jour l'état local et localStorage
      if (success) {
        setPreferences(newPreferences);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newPreferences));
        setError(null);
        return true;
      } else {
        setError(new Error("Échec de la sauvegarde des préférences"));
        return false;
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour des préférences:', err);
      setError(err);
      return false;
    }
  }, [user, preferences]);

  const resetPreferences = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setIsLoading(true);
      await initializePreferences(user.uid, {
        language: user.language
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, initializePreferences]);

  const syncWithFirestore = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setIsLoading(true);
      const firestorePrefs = await loadFirestorePreferences(user.uid);
      if (firestorePrefs) {
        setPreferences(firestorePrefs);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(firestorePrefs));
      }
      setError(null);
    } catch (error) {
      console.error('Erreur lors de la synchronisation avec Firestore:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [user, loadFirestorePreferences]);

  const value = {
    preferences,
    isLoading,
    error,
    isInitialized,
    updatePreference,
    updatePreferences,
    resetPreferences,
    syncWithFirestore
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferencesContext = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferencesContext doit être utilisé dans un PreferencesProvider');
  }
  return context;
};